import { useContext, useEffect } from 'react';
import { VideoErrorBoundary, type VideoErrorBoundaryFallbackProps } from './VideoErrorBoundary.js';
import { VideoOverlay, type VideoOverlayProps } from './VideoOverlay.js';
import { VideoViewContext } from '../../contexts/videoViewContext.js';
import { VIDEO_CONFIG, getStackPositionClass } from '../../config/videoConfig.js';
import { useOpenDrawers } from '../../hooks/useOpenDrawers.js';

/**
 * The video heads-up display, isolated behind an error boundary.
 *
 * This is what the rest of the app mounts. {@link VideoOverlay} renders third
 * party components around live media tracks — a throw inside a tile used to
 * unmount the *entire* React HUD, taking the chat, the bottom bar and every
 * modal with it, because nothing stood between the overlay and `GameUI`
 * (#80). Video is the most failure-prone and least essential part of the
 * screen, so it fails on its own.
 *
 * The props go straight to the overlay: what full screen needs from the rest
 * of the game UI (the call's label, the chat drawer's width).
 */
export function VideoWrapper(props: VideoOverlayProps) {
  return (
    <VideoErrorBoundary fallbackComponent={VideoErrorFallback}>
      <VideoOverlay {...props} />
    </VideoErrorBoundary>
  );
}

const SEVERITY_EMOJI: Record<string, string> = {
  critical: '💥',
  high: '⚠️',
  medium: '⚡',
  low: '🔧'
};

const CATEGORY_ICON: Record<string, string> = {
  network: '🌐',
  permission: '🔒',
  server: '🖥️',
  authentication: '🔐',
  device: '📹',
  configuration: '⚙️'
};

/**
 * Game-themed fallback for a crashed video tree.
 *
 * Shows the *classified* message and the classifier's recovery advice rather
 * than a stack trace, and offers two ways out: retry, which remounts the
 * overlay, and dismiss, which leaves the rest of the HUD alone and renders
 * nothing until the next retry.
 */
function VideoErrorFallback({
  error,
  errorClassification,
  errorId,
  retry,
  dismiss
}: VideoErrorBoundaryFallbackProps) {
  // A crash takes the full-screen overlay down with it; go back to the world
  // view rather than stay "full screen" with nothing on screen (#123). Read
  // optionally: the boundary also renders without a VideoViewProvider.
  const exitFullScreen = useContext(VideoViewContext)?.exitFullScreen;
  useEffect(() => {
    exitFullScreen?.();
  }, [exitFullScreen]);

  const openDrawers = useOpenDrawers();

  return (
    // Where the tiles were: the strip lets clicks through, the panel takes them.
    <div
      className={`${getStackPositionClass(openDrawers)} ${VIDEO_CONFIG.styles.zIndex} pointer-events-none`}
      data-lk-theme={VIDEO_CONFIG.theme}
      data-testid="video-error-fallback"
    >
      <div
        role="alert"
        className="pointer-events-auto w-80 animate-in slide-in-from-top-2 duration-200 bg-game-dark/95 border border-game-accent/30 rounded-lg shadow-2xl p-4"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-lg" aria-hidden="true">
              {SEVERITY_EMOJI[errorClassification.severity] ?? '❓'}
            </span>
            <span className="text-sm opacity-75" aria-hidden="true">
              {CATEGORY_ICON[errorClassification.category] ?? '🔄'}
            </span>
            <h3 className="font-bold text-white">Video Issue</h3>
          </div>
          <button
            onClick={dismiss}
            className="text-gray-400 hover:text-white transition-colors p-1 rounded hover:bg-white/10"
            aria-label="Dismiss video error"
            title="Dismiss"
          >
            ✕
          </button>
        </div>

        <p className="text-sm text-gray-300 leading-relaxed mb-4">
          {errorClassification.userMessage}
        </p>

        {errorClassification.recoveryActions.length > 0 && (
          <div className="mb-4">
            <h4 className="text-xs font-medium mb-2 text-game-accent uppercase tracking-wide">
              What you can do
            </h4>
            <ul className="space-y-1 text-xs text-gray-400 list-disc list-inside">
              {errorClassification.recoveryActions.slice(0, 2).map((action) => (
                <li key={action.label}>{action.description}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex gap-2">
          <button
            onClick={retry}
            className="flex-1 bg-game-accent/20 hover:bg-game-accent/30 border border-game-accent/40 rounded px-3 py-2 text-xs font-medium text-white transition-all duration-200"
          >
            🔄 Try Again
          </button>
          <button
            onClick={dismiss}
            className="px-3 py-2 text-xs font-medium text-gray-400 hover:text-white transition-colors duration-200 hover:bg-white/10 rounded"
          >
            Hide
          </button>
        </div>

        <details className="mt-3 text-xs">
          <summary className="cursor-pointer text-gray-500 hover:text-gray-400 select-none">
            Technical Details
          </summary>
          <div className="mt-2 p-2 bg-black/30 rounded font-mono text-xs border border-gray-700">
            <div className="grid grid-cols-[auto,1fr] gap-x-2 gap-y-1">
              <span className="text-gray-400">Error:</span>
              <span className="text-white">{error.name}</span>
              <span className="text-gray-400">Code:</span>
              <span className="text-white">{errorClassification.category}</span>
              <span className="text-gray-400">ID:</span>
              <span className="text-white">{errorId}</span>
            </div>
          </div>
        </details>
      </div>
    </div>
  );
}
